/**
 * Centralized logging utility
 * - Only logs in development mode
 * - Supports different log levels
 * - Can send errors to Sentry in production
 */

import * as Sentry from '@sentry/react';

const isDevelopment = import.meta.env.DEV;
const isProduction = import.meta.env.PROD;

/**
 * Logger utility with different log levels
 */
const logger = {
    /**
     * Log informational messages (blue)
     * Use for general debugging information
     */
    info: (...args) => {
        if (isDevelopment) {
            console.log('%c[INFO]', 'color: #3b82f6; font-weight: bold', ...args);
        }
    },

    /**
     * Log warning messages (yellow)
     * Use for non-critical issues
     */
    warn: (...args) => {
        if (isDevelopment) {
            console.warn('%c[WARN]', 'color: #f59e0b; font-weight: bold', ...args);
        }
    },

    /**
     * Log error messages (red)
     * Use for errors that need attention
     * In production, can optionally send to Sentry
     */
    error: (...args) => {
        if (isDevelopment) {
            console.error('%c[ERROR]', 'color: #ef4444; font-weight: bold', ...args);
        }

        // In production, send critical errors to Sentry
        if (isProduction) {
            const error = args.find(arg => arg instanceof Error) || new Error(args.join(' '));
            Sentry.captureException(error, {
                extra: args.filter(arg => !(arg instanceof Error))
            });
        }
    },

    /**
     * Log debug messages (gray)
     * Use for detailed debugging (can be disabled)
     */
    debug: (...args) => {
        if (isDevelopment && import.meta.env.VITE_ENABLE_DEBUG === 'true') {
            console.log('%c[DEBUG]', 'color: #6b7280; font-weight: bold', ...args);
        }
    },

    /**
     * Log success messages (green)
     * Use for successful operations
     */
    success: (...args) => {
        if (isDevelopment) {
            console.log('%c[SUCCESS]', 'color: #10b981; font-weight: bold', ...args);
        }
    },

    /**
     * Group related logs together
     */
    group: (label, callback) => {
        if (isDevelopment) {
            console.group(`%c${label}`, 'color: #8b5cf6; font-weight: bold');
            callback();
            console.groupEnd();
        }
    },

    /**
     * Log API requests/responses
     */
    api: {
        request: (method, url, data) => {
            if (isDevelopment) {
                console.log(
                    '%c[API REQUEST]',
                    'color: #6366f1; font-weight: bold',
                    `${method} ${url}`,
                    data ? { data } : ''
                );
            }
        },
        response: (method, url, response) => {
            if (isDevelopment) {
                console.log(
                    '%c[API RESPONSE]',
                    'color: #10b981; font-weight: bold',
                    `${method} ${url}`,
                    response
                );
            }
        },
        error: (method, url, error) => {
            if (isDevelopment) {
                console.error(
                    '%c[API ERROR]',
                    'color: #ef4444; font-weight: bold',
                    `${method} ${url}`,
                    error
                );
            }

            // Send API errors to Sentry in production
            if (isProduction) {
                Sentry.captureException(error, {
                    tags: { api: true, method, url },
                    extra: { method, url }
                });
            }
        }
    }
};

export default logger;
